import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../Styles/SearchPage.css';
import '../Styles/Home.css'; // 引入動畫樣式

const SearchPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialQuery = queryParams.get('q') || '';

  // 狀態管理
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [contentToSearch, setContentToSearch] = useState({});
  const [translationsLoaded, setTranslationsLoaded] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false); // 加入動畫載入狀態
  
  // 頁面動畫載入效果
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // 動態載入翻譯文件與硬編碼內容
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        console.log('开始加载翻译文件...');

        // 取得各語言翻譯文件
        const fetchTranslation = async (lang) => {
          const response = await fetch(`/locales/${lang}/translation.json`);
          if (!response.ok) {
            throw new Error(`无法加载${lang}翻译文件: ${response.status}`);
          }
          return response.json();
        };

        const enData = await fetchTranslation('en');
        console.log('英文翻譯文件加载成功');
        const zhData = await fetchTranslation('zh');
        console.log('中文翻譯文件加载成功');
        const jaData = await fetchTranslation('ja');
        console.log('日文翻譯文件加载成功');

        // 硬編碼的內容，確保至少有基本頁面可搜尋
        const hardcodedContent = {
          sidebar: {
            home: '首頁',
            valuationModels: '估值模型',
            investmentBasics: '投資基礎',
            pe: '市盈率',
            ps: '市銷率',
            pb: '市淨率',
            dcf: 'DCF模型',
            ddm: 'DDM模型',
          },
          navigation: {
            home: '首頁',
            login: '登入',
            search: '搜索',
            settings: '設定',
          },
          components: {
            sidebar: '側邊欄',
            topbar: '頂部導航欄',
            breadcrumb: '麵包屑導航',
          },
        };

        // 將硬編碼內容與翻譯文件合併
        const enhancedEnData = { ...enData, hardcoded: hardcodedContent };
        const enhancedZhData = { ...zhData, hardcoded: hardcodedContent };
        const enhancedJaData = { ...jaData, hardcoded: hardcodedContent };

        setContentToSearch({
          en: enhancedEnData,
          zh: enhancedZhData,
          ja: enhancedJaData,
        });
        console.log('所有翻譯文件加载完成，内容已设置');
      } catch (loadError) {
        console.error('加载翻译文件时发生错误:', loadError);
        // 若翻譯文件載入失敗，使用備用內容
        const fallbackContent = {
          fallback: {
            home: '首頁/Home/ホーム',
            valuationModels: '估值模型/Valuation Models/評価モデル',
            investmentBasics: '投資基礎/Investment Basics/投資の基本',
            sidebar: '側邊欄/Sidebar/サイドバー',
            topbar: '頂部欄/TopBar/トップバー',
            pe: '市盈率/P/E Ratio/PER',
            ps: '市銷率/P/S Ratio/PSR',
            pb: '市淨率/P/B Ratio/PBR',
          },
        };
        setContentToSearch({
          en: fallbackContent,
          zh: fallbackContent,
          ja: fallbackContent,
        });
        console.log('使用备用内容设置搜索数据');
      } finally {
        // 無論成功或失敗，都設定翻譯已載入完成
        setTranslationsLoaded(true);
      }
    };

    loadTranslations();
  }, [initialQuery]);

  // 當初始查詢或翻譯載入完成後執行搜索
  useEffect(() => {
    if (initialQuery && translationsLoaded) {
      performSearch(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery, translationsLoaded, i18n.language]);

  // 處理搜索提交
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) {
      // 更新URL中的查詢參數，方便共享與重新整理頁面時保留查詢
      navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
      performSearch(trimmedQuery);
    }
  };

  // 搜索邏輯
  const performSearch = (query) => {
    setIsLoading(true);
    console.log('开始执行搜索，查询词:', query);
    // 呼叫搜索函數
    try {
      const results = searchInContent(query);
      console.log('搜索完成，结果数量:', results.length);
      setSearchResults(results);
    } catch (error) {
      console.error('搜索过程中发生错误:', error);
      // 搜索失敗時，添加緊急結果
      setSearchResults([
        {
          path: 'emergency.home',
          text: '首页',
          navTarget: '/',
          textSnippet: `<span class="highlight">首页</span>`,
          priority: 1,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // 搜索函數：在內容中搜索匹配結果
  const searchInContent = (query) => {
    if (!query) {
      console.log('查询词为空，返回空结果');
      return [];
    }
    if (Object.keys(contentToSearch).length === 0) {
      console.log('没有可搜索的内容，返回空结果');
      return [];
    }

    const results = [];
    // 標準化語言代碼（只取前兩個字母，例如 en-US -> en）
    const currentLang = i18n.language.slice(0, 2);
    console.log('当前语言:', currentLang);

    // 從 contentToSearch 中選取合適的語言內容，若沒有則使用英文
    const content = contentToSearch[currentLang] || contentToSearch.en || {};
    console.log('获取到搜索内容，内容键:', Object.keys(content).join(', '));

    const lowerCaseQuery = query.toLowerCase();
    console.log('转换为小写的查询词:', lowerCaseQuery);

    // 硬編碼項目：主要 UI 元素與頁面
    const hardcodedItems = [
      {
        key: '首頁',
        path: 'home',
        targets: ['首頁', '首页', 'home', 'ホーム', '主页', '网站首页'],
        navTarget: '/',
      },
      {
        key: '估值模型',
        path: 'valuation-models',
        targets: ['估值模型', 'valuation models', '評価モデル', '价值评估'],
        navTarget: '/valuation-models',
      },
      {
        key: '投資基礎',
        path: 'investment-basics',
        targets: [
          '投資基礎',
          '投资基础',
          'investment basics',
          '投資の基本',
          '投资知识',
        ],
        navTarget: '/investment-basics',
      },
      {
        key: '市盈率',
        path: 'pe',
        targets: [
          '市盈率',
          'P/E Ratio',
          'PE',
          'P/E',
          'PER',
          '株価収益率',
          '市盈比',
        ],
        navTarget: '/valuation-models/pe',
      },
      {
        key: '市銷率',
        path: 'ps',
        targets: [
          '市銷率',
          '市销率',
          'P/S Ratio',
          'PS',
          'P/S',
          'PSR',
          '株価売上高倍率',
          '市销比',
        ],
        navTarget: '/valuation-models/ps',
      },
      {
        key: '市淨率',
        path: 'pb',
        targets: [
          '市淨率',
          '市净率',
          'P/B Ratio',
          'PB',
          'P/B',
          'PBR',
          '株価純資産倍率',
          '市净比',
        ],
        navTarget: '/valuation-models/pb',
      },
      {
        key: 'DCF',
        path: 'dcf',
        targets: [
          'DCF',
          '贴现现金流',
          'Discounted Cash Flow',
          '割引キャッシュフロー',
        ],
        navTarget: '/valuation-models/dcf',
      },
      {
        key: 'DDM',
        path: 'ddm',
        targets: [
          'DDM',
          '股息贴现模型',
          'Dividend Discount Model',
          '配当割引モデル',
        ],
        navTarget: '/valuation-models/ddm',
      },
      {
        key: '側邊欄',
        path: 'sidebar',
        targets: ['側邊欄', '侧边栏', 'sidebar', 'サイドバー', '导航栏'],
        navTarget: '/',
      },
      {
        key: '頂部欄',
        path: 'topbar',
        targets: [
          '頂部欄',
          '顶部栏',
          'topbar',
          'トップバー',
          '顶部导航',
          '导航条',
        ],
        navTarget: '/',
      },
      {
        key: '麵包屑',
        path: 'breadcrumb',
        targets: [
          '麵包屑',
          '面包屑',
          'breadcrumb',
          'パンくずリスト',
          '导航路径',
        ],
        navTarget: '/',
      },
      {
        key: '搜索',
        path: 'search',
        targets: ['搜索', 'search', '搜尋', '検索'],
        navTarget: '/search',
      },
      {
        key: '登入',
        path: 'login',
        targets: ['登入', '登录', 'login', 'ログイン', '会员登录', '用户登录'],
        navTarget: '/login',
      },
      {
        key: '暗黑模式',
        path: 'dark-mode',
        targets: [
          '暗黑模式',
          '深色模式',
          'dark mode',
          'ダークモード',
          '夜间模式',
        ],
        navTarget: '/',
      },
    ];

    console.log('检查硬编码项目匹配情况...');
    hardcodedItems.forEach((item) => {
      if (
        item.targets.some(
          (target) =>
            target.toLowerCase() === lowerCaseQuery ||
            target.toLowerCase().includes(lowerCaseQuery) ||
            lowerCaseQuery.includes(target.toLowerCase())
        )
      ) {
        console.log('匹配到硬编码项目:', item.key);
        let displayText = item.key;
        // 根據語言決定顯示文本
        if (currentLang === 'en') {
          const enTarget =
            item.targets.find((t) => /^[a-zA-Z\s]+$/.test(t)) ||
            item.targets[0];
          displayText = enTarget;
        } else if (currentLang === 'ja') {
          const jaTarget =
            item.targets.find((t) => /[\u3040-\u309F\u30A0-\u30FF]/.test(t)) ||
            item.targets[0];
          displayText = jaTarget;
        } else if (currentLang === 'zh') {
          const zhTarget =
            item.targets.find((t) => /[\u4E00-\u9FFF]/.test(t)) ||
            item.targets[0];
          displayText = zhTarget;
        }
        results.push({
          path: `基本項目.${item.path}`,
          text: displayText,
          navTarget: item.navTarget,
          textSnippet: `<span class="highlight">${displayText}</span> - 網站UI元素`,
          priority: 1,
        });
      }
    });

    // 簡單路徑匹配（基本頁面）
    const simpleRoutes = [
      {
        path: '/',
        name: '首页',
        keywords: ['首页', '主页', 'home', '首頁', 'ホーム'],
      },
      {
        path: '/valuation-models',
        name: '估值模型',
        keywords: [
          '估值模型',
          'valuation models',
          '估值',
          '模型',
          '評価モデル',
        ],
      },
      {
        path: '/investment-basics',
        name: '投资基础',
        keywords: [
          '投资基础',
          '投資基礎',
          'investment basics',
          '投資',
          '基礎',
          '基本',
          '投資の基本',
        ],
      },
      {
        path: '/equity-research',
        name: '股票研究',
        keywords: ['股票研究', 'equity research', '股票', '研究', '株式調査'],
      },
      {
        path: '/ma-analysis',
        name: '并购分析',
        keywords: ['并购分析', 'M&A analysis', '并购', 'M&A', '合併と買収'],
      },
      {
        path: '/about',
        name: '关于我们',
        keywords: ['关于', '关于我们', 'about', 'about us', '私たちについて'],
      },
      {
        path: '/login',
        name: '会员登录',
        keywords: ['登录', '会员登录', 'login', '登入', 'ログイン'],
      },
      {
        path: '/search',
        name: '搜索',
        keywords: ['搜索', 'search', '搜尋', '検索'],
      },
    ];

    console.log('检查简单路径匹配...');
    simpleRoutes.forEach((route) => {
      if (
        route.keywords.some(
          (keyword) =>
            keyword.toLowerCase().includes(lowerCaseQuery) ||
            lowerCaseQuery.includes(keyword.toLowerCase())
        )
      ) {
        console.log('匹配到简单路径:', route.name);
        results.push({
          path: `路径.${route.path}`,
          text: route.name,
          navTarget: route.path,
          textSnippet: `<span class="highlight">${route.name}</span> - 网站页面`,
          priority: 2,
        });
      }
    });

    // 遞歸搜索翻譯內容中的所有字符串
    const searchObject = (obj, path = '') => {
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          const currentPath = path ? `${path}.${key}` : key;
          if (typeof obj[key] === 'string') {
            const value = obj[key];
            if (
              value.toLowerCase().includes(lowerCaseQuery) ||
              lowerCaseQuery.includes(value.toLowerCase())
            ) {
              console.log('在翻译中找到匹配:', currentPath, value);
              const navTarget = getNavigationTarget(currentPath);
              results.push({
                path: currentPath,
                text: value,
                navTarget,
                textSnippet: getTextSnippet(value, lowerCaseQuery),
                priority: 3,
              });
            }
          } else if (Array.isArray(obj[key])) {
            obj[key].forEach((item, index) => {
              if (
                typeof item === 'string' &&
                item.toLowerCase().includes(lowerCaseQuery)
              ) {
                console.log(
                  '在数组中找到匹配:',
                  `${currentPath}[${index}]`,
                  item
                );
                results.push({
                  path: `${currentPath}[${index}]`,
                  text: item,
                  navTarget: getNavigationTarget(currentPath),
                  textSnippet: getTextSnippet(item, lowerCaseQuery),
                  priority: 3,
                });
              } else if (typeof item === 'object' && item !== null) {
                searchObject(item, `${currentPath}[${index}]`);
              }
            });
          } else if (typeof obj[key] === 'object' && obj[key] !== null) {
            searchObject(obj[key], currentPath);
          }
        }
      }
    };

    // 遍歷所有語言的內容
    for (const lang in contentToSearch) {
      if (contentToSearch[lang]) {
        console.log(`搜索${lang}语言内容...`);
        searchObject(contentToSearch[lang], `${lang}`);
      }
    }

    console.log('搜索完成，结果数量:', results.length);
    if (results.length === 0) {
      console.log('没有找到结果，添加紧急结果');
      const allItems = [...hardcodedItems, ...simpleRoutes];
      let bestMatch = null;
      let bestScore = 0;

      allItems.forEach((item) => {
        const keywords = item.targets || item.keywords;
        keywords.forEach((keyword) => {
          let score = 0;
          const lowerKeyword = keyword.toLowerCase();
          if (
            lowerKeyword.includes(lowerCaseQuery) ||
            lowerCaseQuery.includes(lowerKeyword)
          ) {
            score =
              Math.min(lowerKeyword.length, lowerCaseQuery.length) /
              Math.max(lowerKeyword.length, lowerCaseQuery.length);
          }
          if (score > bestScore) {
            bestScore = score;
            bestMatch = {
              item: item,
              keyword: keyword,
            };
          }
        });
      });

      if (bestMatch && bestScore > 0.2) {
        console.log(
          '添加最佳匹配作为紧急结果:',
          bestMatch.item.key || bestMatch.item.name
        );
        const emergencyResult = {
          path: '紧急结果',
          text: bestMatch.item.key || bestMatch.item.name,
          navTarget: bestMatch.item.navTarget || bestMatch.item.path,
          textSnippet: `<span class="highlight">${bestMatch.keyword}</span> - 可能的匹配结果`,
          priority: 1,
        };
        results.push(emergencyResult);
      } else {
        ['home', ' valuation model', 'investment basic'].forEach(
          (name, index) => {
            results.push({
              path: 'recommend page',
              text: name,
              navTarget:
                index === 0
                  ? '/'
                  : index === 1
                    ? '/valuation-models'
                    : '/investment-basics',
              textSnippet: `<span class="highlight">${name}</span> - recommend page`,
              priority: 10 + index,
            });
          }
        );
      }
    }

    // 按优先级排序搜索結果
    results.sort((a, b) => (a.priority || 99) - (b.priority || 99));
    return results;
  };

  // 根據路徑決定導航目標
  const getNavigationTarget = (path) => {
    if (path.startsWith('pe') || path.includes('.pe.')) {
      return '/valuation-models/pe';
    } else if (path.startsWith('ps') || path.includes('.ps.')) {
      return '/valuation-models/ps';
    } else if (path.startsWith('pb') || path.includes('.pb.')) {
      return '/valuation-models/pb';
    } else if (path.startsWith('dcf') || path.includes('.dcf.')) {
      return '/valuation-models/dcf';
    } else if (path.startsWith('ddm') || path.includes('.ddm.')) {
      return '/valuation-models/ddm';
    } else if (
      path.startsWith('investmentBasics') ||
      path.includes('.investmentBasics.')
    ) {
      return '/investment-basics';
    } else if (
      path.includes('typesOfInvestments') ||
      path.includes('.typesOfInvestments.')
    ) {
      return '/investment-basics/types-of-investments';
    } else if (
      path.includes('riskAndReturn') ||
      path.includes('.riskAndReturn.')
    ) {
      return '/investment-basics/risk-and-reward';
    } else if (
      path.includes('investingPage') ||
      path.includes('.investingPage.')
    ) {
      return '/investment-basics/what-is-investing';
    } else if (path.startsWith('home') || path.includes('.home.')) {
      return '/';
    } else if (path.startsWith('breadcrumb') || path.includes('.breadcrumb.')) {
      return '/';
    } else {
      return '/';
    }
  };

  // 提取文本片段，並高亮查詢關鍵詞
  const getTextSnippet = (text, query) => {
    const lowerText = text.toLowerCase();
    const index = lowerText.indexOf(query);
    if (index === -1) return text;
    const snippetLength = 100;
    let start = Math.max(0, index - snippetLength / 2);
    let end = Math.min(text.length, index + query.length + snippetLength / 2);
    const prefix = start > 0 ? '...' : '';
    const suffix = end < text.length ? '...' : '';
    const snippet = text.substring(start, end);
    const highlightedSnippet = snippet.replace(
      new RegExp(query, 'gi'),
      (match) => `<span class="highlight">${match}</span>`
    );
    return prefix + highlightedSnippet + suffix;
  };

  // 點擊搜索結果後導航
  const navigateToResult = (navTarget) => {
    navigate(navTarget);
  };

  return (
    <div className={`search-page ${isLoaded ? 'content-loaded' : 'content-hidden'}`}>
      <h1 className={`${isLoaded ? 'fade-in-element' : ''}`}>{t('search')}</h1>
      <form onSubmit={handleSearchSubmit} className={`search-form ${isLoaded ? 'fade-in-element delay-1' : ''}`}>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder={t('searchPlaceholder') || '输入关键词搜索...'}
          className="search-input"
        />
        <button type="submit" className="search-button">
          {t('searchButton') || '搜索'}
        </button>
      </form>
      <div className={`search-results ${isLoaded ? 'fade-in-element delay-2' : ''}`}>
        {isLoading ? (
          <div className="loading">{t('searching') || '正在搜索...'}...</div>
        ) : (
          <>
            {searchResults.length > 0 ? (
              <>
                <div className={`results-count ${isLoaded ? 'fade-in-element delay-2' : ''}`}>
                  {t('resultsCount', { count: searchResults.length }) ||
                    `找到 ${searchResults.length} 个结果`}
                </div>
                <ul className={`results-list ${isLoaded ? 'fade-in-element delay-3' : ''}`}>
                  {searchResults.map((result, index) => (
                    <li
                      key={index}
                      className="result-item"
                      onClick={() => navigateToResult(result.navTarget)}
                    >
                      <div className="result-path">{result.path}</div>
                      <div
                        className="result-snippet"
                        dangerouslySetInnerHTML={{ __html: result.textSnippet }}
                      />
                    </li>
                  ))}
                </ul>
              </>
            ) : searchQuery ? (
              <div className={`no-results ${isLoaded ? 'fade-in-element delay-2' : ''}`}>
                {t('noResults', { query: searchQuery }) ||
                  ` '${searchQuery}' 相关的结果`}
                <div className="no-results-suggestion">
                  Try to press Search Button
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default SearchPage;
